import { compact } from 'lodash-es';

import { TOperatingHours, TWeekday } from 'types/detailsEntityType';

const DAYS: TWeekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_LABEL = Object.fromEntries(
  DAYS.map((day) => [day, day[0] + day.slice(1).toLowerCase()])
) as Record<TWeekday, string>;

export enum OperatingHoursKind {
  NOT_FOUND = 'NOT_FOUND',
  ALWAYS_ON = 'ALWAYS_ON',
  UNIFORM = 'UNIFORM',
  PER_DAY = 'PER_DAY',
}

type TDaySchedules = { label: string; ranges: string[] }[];

export type TOperatingHoursView =
  | { kind: OperatingHoursKind.NOT_FOUND }
  | { kind: OperatingHoursKind.ALWAYS_ON }
  | { kind: OperatingHoursKind.UNIFORM; ranges: string[] }
  | { kind: OperatingHoursKind.PER_DAY; days: TDaySchedules };

export type TChipItem = {
  key: string;
  label: string;
  days?: TDaySchedules;
};

const scheduleHash = (days: TDaySchedules): string =>
  days.map((day) => `${day.label}:${day.ranges.join(',')}`).join('|');

export const toChipItems = (view: TOperatingHoursView): TChipItem[] => {
  switch (view.kind) {
    case OperatingHoursKind.NOT_FOUND:
      return [];
    case OperatingHoursKind.ALWAYS_ON:
      return [{ key: 'always-on', label: 'Always On' }];
    case OperatingHoursKind.UNIFORM:
      return view.ranges.map((range) => ({ key: `range:${range}`, label: range }));
    case OperatingHoursKind.PER_DAY:
      return [
        {
          key: `perday:${scheduleHash(view.days)}`,
          label: 'Variable Hours',
          days: view.days,
        },
      ];
  }
};

type TDayRanges = { day: TWeekday; ranges: string[] };

const getDaysWithRanges = (operHours: TOperatingHours): TDayRanges[] =>
  DAYS.map((day) => ({ day, ranges: compact(operHours[day]) }));

const hasNoRanges = (perDay: TDayRanges[]): boolean =>
  perDay.every(({ ranges }) => ranges.length === 0);

const hasUniformRanges = (perDay: TDayRanges[]): boolean => {
  const firstKey = perDay[0].ranges.join('|');
  return perDay.every(({ ranges }) => ranges.join('|') === firstKey);
};

const isAlwaysOn = (ranges: string[]): boolean =>
  ranges.length === 1 && ranges[0] === '00:00 - 23:59';

const toPerDayView = (perDay: TDayRanges[]): TDaySchedules =>
  perDay.map(({ day, ranges }) => ({ label: DAY_LABEL[day], ranges }));

export const formatOperatingHours = (operHours: TOperatingHours | null): TOperatingHoursView => {
  if (!operHours) return { kind: OperatingHoursKind.NOT_FOUND };

  const perDay = getDaysWithRanges(operHours);

  if (hasNoRanges(perDay)) return { kind: OperatingHoursKind.NOT_FOUND };

  if (hasUniformRanges(perDay)) {
    const { ranges } = perDay[0];
    if (isAlwaysOn(ranges)) return { kind: OperatingHoursKind.ALWAYS_ON };
    return { kind: OperatingHoursKind.UNIFORM, ranges };
  }

  return { kind: OperatingHoursKind.PER_DAY, days: toPerDayView(perDay) };
};
